import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardMedia,
  Grid,
  useTheme,
  useMediaQuery,
  Fade,
  Skeleton,
  Alert,
  Link as MuiLink
} from '@mui/material';
import {
  Login as LoginIcon,
  Celebration as CelebrationIcon,
  PersonAdd as PersonAddIcon,
  AdminPanelSettings as AdminIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { FeaturedMediaDisplay } from './FeaturedMediaDisplay';
import { useCustomTheme } from './ThemeProvider';

interface CelebrationInfo {
  hostName: string;
  milestone: string;
  dates: string;
  location: string;
  description: string;
}

interface HostPhoto {
  url: string;
  alt: string;
}

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [celebrationInfo, setCelebrationInfo] = useState<CelebrationInfo | null>(null);
  const [hostPhoto, setHostPhoto] = useState<HostPhoto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);


  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { siteSettings, loading: themeLoading } = useCustomTheme();

  // Fetch celebration info and host photo
  useEffect(() => {
    const fetchLandingPageData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use generic placeholder data - actual content comes from site settings
        const mockData: CelebrationInfo = {
          hostName: "",
          milestone: "",
          dates: "",
          location: "",
          description: ""
        };

        const mockHostPhoto: HostPhoto = {
          url: "/sample-media/birthday-host.jpg",
          alt: "Event Photo"
        };

        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));

        setCelebrationInfo(mockData);
        setHostPhoto(mockHostPhoto);
      } catch (err) {
        setError('Failed to load celebration information');
        console.error('Error fetching landing page data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLandingPageData();
  }, []);

  // Get custom content from site settings
  const welcomeTitle = siteSettings?.content.welcome.title || 'Welcome to Our Event';
  const welcomeMessage = siteSettings?.content.welcome.message || 'We\'re excited to have you join us!';
  const tagline = siteSettings?.content.welcome.tagline || 'Making Memories Together';
  const heroImage = siteSettings?.images.hero?.url || hostPhoto?.url;
  const logoImage = siteSettings?.images.logo;

  const handleGuestLogin = () => {
    // Navigate to guest login
    navigate('/login');
  };

  const handleRequestAccess = () => {
    // Open email client with pre-filled information
    const subject = encodeURIComponent('Guest Access Request');
    const body = encodeURIComponent(
      'Hello,\n\n' +
      'I would like to request access to the event.\n\n' +
      'Full Name: \n' +
      'Email: \n' +
      'Phone: \n' +
      'Relationship to Host: \n' +
      'Message: \n\n' +
      'Thank you!'
    );
    window.location.href = `mailto:lb50cartagena@gmail.com?subject=${subject}&body=${body}`;
  };

  if (loading || themeLoading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="text" height={60} />
              <Skeleton variant="text" height={40} />
              <Skeleton variant="text" height={120} />
              <Skeleton variant="rectangular" height={50} sx={{ mt: 2, borderRadius: 1 }} />
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="sm">
          <Alert severity="error" sx={{ textAlign: 'center' }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'var(--gradient-background, linear-gradient(135deg, #667eea 0%, #764ba2 100%))',
          color: 'white',
          py: { xs: 4, md: 8 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          {/* Display custom logo if available */}
          {logoImage && (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <img 
                src={logoImage.url} 
                alt={logoImage.alt || 'Event Logo'} 
                loading="eager"
                decoding="async"
                style={{ 
                  maxHeight: isMobile ? '60px' : '80px', 
                  maxWidth: isMobile ? '150px' : '200px',
                  width: 'auto',
                  height: 'auto'
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </Box>
          )}
          
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
              <Fade in timeout={1000}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CelebrationIcon sx={{ fontSize: 40, mr: 2 }} />
                    <Typography
                      variant={isMobile ? "h4" : "h3"}
                      component="h1"
                      fontWeight="bold"
                    >
                      {welcomeTitle}
                    </Typography>
                  </Box>
                  
                  <Typography
                    variant={isMobile ? "h5" : "h4"}
                    component="h2"
                    sx={{ mb: 3, opacity: 0.9 }}
                  >
                    {tagline}
                  </Typography>
                  
                  <Typography
                    variant="body1"
                    sx={{ mb: 4, lineHeight: 1.6, opacity: 0.9 }}
                    dangerouslySetInnerHTML={{ __html: welcomeMessage }}
                  />
                  
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<LoginIcon />}
                      onClick={handleGuestLogin}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.3)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Guest Login
                    </Button>
                    
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<PersonAddIcon />}
                      onClick={handleRequestAccess}
                      sx={{
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Request Access
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            
            <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
              <Fade in timeout={1200}>
                <Card
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                    transform: isMobile ? 'none' : 'rotate(2deg)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: isMobile ? 'none' : 'rotate(0deg) scale(1.02)'
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    height={isMobile ? 300 : 400}
                    image={heroImage}
                    alt={siteSettings?.images.hero?.alt || hostPhoto?.alt || 'Event Hero Image'}
                    loading="eager"
                    sx={{ objectFit: 'cover' }}
                    onError={(e) => {
                      // Fallback to a placeholder if hero image fails to load
                      const target = e.target as HTMLImageElement;
                      target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yMDAgMTAwQzE3Mi4zODYgMTAwIDE1MCA3Ny42MTQyIDE1MCA1MEMxNTAgMjIuMzg1OCAxNzIuMzg2IDAgMjAwIDBDMjI3LjYxNCAwIDI1MCAyMi4zODU4IDI1MCA1MEMyNTAgNzcuNjE0MiAyMjcuNjE0IDEwMCAyMDAgMTAwWiIgZmlsbD0iI0NDQ0NDQyIvPgo8cGF0aCBkPSJNMzAwIDM1MEMzMDAgMjk0Ljc3MiAyNTUuMjI4IDI1MCAyMDAgMjUwQzE0NC43NzIgMjUwIDEwMCAyOTQuNzcyIDEwMCAzNTBIMzAwWiIgZmlsbD0iI0NDQ0NDQyIvPgo8L3N2Zz4K';
                    }}
                  />
                </Card>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Featured Media Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        <Fade in timeout={1500}>
          <Box>
            <Typography
              variant={isMobile ? "h4" : "h3"}
              component="h2"
              textAlign="center"
              sx={{ mb: 2, fontWeight: 'bold', color: 'text.primary' }}
            >
              Celebration Memories
            </Typography>
            
            <Typography
              variant="h6"
              textAlign="center"
              sx={{ mb: 6, color: 'text.secondary', maxWidth: 600, mx: 'auto' }}
            >
              Discover the beautiful moments our guests are sharing from this special celebration
            </Typography>
            
            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
              <FeaturedMediaDisplay
                context="landing"
                autoRotate={true}
                rotationInterval={6000}
                maxItems={5}
                showUploader={false}
                className="landing-featured-media"
              />
            </Box>
          </Box>
        </Fade>
      </Container>

      {/* Call to Action Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 4, md: 6 },
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Fade in timeout={2000}>
            <Box>
              <Typography
                variant={isMobile ? "h4" : "h3"}
                component="h2"
                sx={{ mb: 3, fontWeight: 'bold' }}
              >
                Join the Celebration
              </Typography>
              
              <Typography
                variant="h6"
                sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6 }}
              >
                Are you an invited guest? Access your personalized schedule, view your clothing assignments, and share your own memories from this special celebration.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexDirection: isMobile ? 'column' : 'row' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<LoginIcon />}
                  onClick={handleGuestLogin}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    '&:hover': {
                      bgcolor: 'grey.100',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Access Guest Portal
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<PersonAddIcon />}
                  onClick={handleRequestAccess}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Request Access
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          bgcolor: 'grey.900',
          color: 'white',
          py: 4,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {/* Contact Information */}
            {(siteSettings?.content.footer.contactEmail || 
              siteSettings?.content.footer.contactPhone || 
              siteSettings?.content.footer.address) && (
              <Grid item xs={12} md={4}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Contact Us
                </Typography>
                {siteSettings?.content.footer.contactEmail && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EmailIcon sx={{ fontSize: 18, mr: 1, opacity: 0.7 }} />
                    <MuiLink 
                      href={`mailto:${siteSettings.content.footer.contactEmail}`}
                      sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {siteSettings.content.footer.contactEmail}
                    </MuiLink>
                  </Box>
                )}
                {siteSettings?.content.footer.contactPhone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PhoneIcon sx={{ fontSize: 18, mr: 1, opacity: 0.7 }} />
                    <MuiLink 
                      href={`tel:${siteSettings.content.footer.contactPhone}`}
                      sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {siteSettings.content.footer.contactPhone}
                    </MuiLink>
                  </Box>
                )}
                {siteSettings?.content.footer.address && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    <LocationIcon sx={{ fontSize: 18, mr: 1, mt: 0.3, opacity: 0.7 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {siteSettings.content.footer.address}
                    </Typography>
                  </Box>
                )}
              </Grid>
            )}

            {/* Social Media Links */}
            {(siteSettings?.content.footer.socialLinks.facebook || 
              siteSettings?.content.footer.socialLinks.instagram || 
              siteSettings?.content.footer.socialLinks.twitter ||
              (siteSettings?.content.footer.socialLinks.custom && siteSettings.content.footer.socialLinks.custom.length > 0)) && (
              <Grid item xs={12} md={4}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Follow Us
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {siteSettings?.content.footer.socialLinks.facebook && (
                    <MuiLink 
                      href={siteSettings.content.footer.socialLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: 'white', '&:hover': { opacity: 0.7 } }}
                    >
                      <FacebookIcon />
                    </MuiLink>
                  )}
                  {siteSettings?.content.footer.socialLinks.instagram && (
                    <MuiLink 
                      href={siteSettings.content.footer.socialLinks.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: 'white', '&:hover': { opacity: 0.7 } }}
                    >
                      <InstagramIcon />
                    </MuiLink>
                  )}
                  {siteSettings?.content.footer.socialLinks.twitter && (
                    <MuiLink 
                      href={siteSettings.content.footer.socialLinks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: 'white', '&:hover': { opacity: 0.7 } }}
                    >
                      <TwitterIcon />
                    </MuiLink>
                  )}
                  {siteSettings?.content.footer.socialLinks.custom?.map((link, index) => (
                    <MuiLink 
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {link.name}
                    </MuiLink>
                  ))}
                </Box>
              </Grid>
            )}

            {/* Copyright and Admin Link */}
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
                {siteSettings?.content.footer.copyrightText || `© ${new Date().getFullYear()} All Rights Reserved`}
              </Typography>
              <Button
                variant="text"
                size="small"
                startIcon={<AdminIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate('/admin')}
                sx={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 0.8)',
                    bgcolor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                Admin Portal
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

    </Box>
  );
};

export default LandingPage;